import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { EditorElement } from '../types/elements';
import { generateImage } from '../utils/imageServerApi';

export const useProjectSync = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const projectId = searchParams.get('project');

  const [projectData, setProjectData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Load project from Supabase
  useEffect(() => {
    if (!projectId || !user) {
      setLoading(false);
      return;
    }

    const loadProject = async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (error) throw error;
        
        // 🔒 Security check: تأكد أن المستخدم الحالي هو مالك المشروع
        if (data.user_id !== user.id) {
          console.error('🚫 Access denied: You do not own this project');
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        
        setProjectData(data);
      } catch (err: any) {
        console.error('Error loading project:', err);
        setAccessDenied(true);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, user]);

  // Save project to Supabase
  const saveProject = useCallback(async (
    elements: EditorElement[],
    canvasWidth: number,
    canvasHeight: number,
    projectName: string
  ) => {
    if (!projectId || !user) return { success: false, error: 'No project ID or user' };

    // 🔒 Security check: تأكد أن المستخدم يملك المشروع قبل الحفظ
    if (projectData && projectData.user_id !== user.id) {
      console.error('🚫 Save denied: You do not own this project');
      return { success: false, error: 'ليس لديك صلاحية لحفظ هذا المشروع' };
    }

    setSaving(true);
    try {
      // Upload ALL images to Supabase (data URIs, blob URLs, and external URLs)
      const processedElements = await Promise.all(
        elements.map(async (el: any) => {
          if (el.type === 'image' && el.src) {
            // Skip ONLY if already on Supabase or is local path
            if (
              el.src.includes('supabase.co') || 
              el.src.startsWith('/') || 
              el.src.startsWith('./') ||
              el.src.startsWith('../')
            ) {
              return el;
            }
            
            // رفع جميع الصور الأخرى (data:, blob:, http:, https:)
            try {
              let blob: Blob;
              let fileExt = 'png';
              
              // Handle data URIs (base64 images)
              if (el.src.startsWith('data:')) {
                console.log('📤 Converting data URI to blob and uploading...');
                
                // Convert data URI to blob
                const response = await fetch(el.src);
                blob = await response.blob();
                
                // Extract extension from data URI
                const mimeMatch = el.src.match(/data:image\/([a-zA-Z]+);/);
                if (mimeMatch) {
                  fileExt = mimeMatch[1];
                }
              } else if (el.src.startsWith('blob:')) {
                // Handle blob URLs (من drag & drop)
                console.log('📤 Converting blob URL to file and uploading...');
                
                const response = await fetch(el.src);
                blob = await response.blob();
                
                // استخراج extension من mime type
                fileExt = blob.type.split('/')[1] || 'png';
              } else {
                // Handle external URLs (http:, https:)
                console.log('📤 Downloading and uploading external image:', el.src.substring(0, 50));
                
                const response = await fetch(el.src);
                blob = await response.blob();
                
                fileExt = el.src.split('.').pop()?.split('?')[0] || 'jpg';
              }
              
              // Upload to Supabase
              const fileName = `${user.id}/${projectId}/${Date.now()}.${fileExt}`;
              
              console.log('☁️ Uploading to Storage:', fileName);
              const { error: uploadError } = await supabase.storage
                .from('project-images')
                .upload(fileName, blob);
              
              if (uploadError) {
                console.warn('⚠️ Failed to upload image, keeping original:', uploadError);
                return el;
              }
              
              // Get public URL
              const { data: urlData } = supabase.storage
                .from('project-images')
                .getPublicUrl(fileName);
              
              console.log('✅ Image uploaded successfully:', urlData.publicUrl);
              
              // استبدال المعاينة المؤقتة بـ URL الدائم
              return { ...el, src: urlData.publicUrl };
            } catch (err) {
              console.warn('⚠️ Failed to upload image, keeping original:', err);
              return el;
            }
          }
          return el;
        })
      );
      
      // جلب الخطوط المخصصة للمستخدم
      let customFonts: any[] = [];
      try {
        const { data: fontsData } = await supabase
          .from('custom_fonts')
          .select('id, font_name, font_file_url, font_family_css')
          .eq('user_id', user.id);
        
        customFonts = fontsData || [];
        console.log('📝 Loaded custom fonts for HTML:', customFonts.length);
      } catch (err) {
        console.warn('⚠️ Failed to load custom fonts:', err);
      }

      // Generate HTML template for runner (use processed elements with uploaded images)
      const generateHTML = () => {
        const containerStyle = `position:relative;width:${canvasWidth}px;height:${canvasHeight}px;background:#fff;overflow:hidden;`;
        let childrenHtml = '';
        
        processedElements.forEach((el: any) => {
          // Skip elements hidden from runner
          if (el.hideInRunner) {
            // Render as fixed element (not placeholder)
            if (el.type === 'text') {
              const textAlign = el.kashidaAlign === 'justify' ? 'justify' : (el.textAlign || 'right');
              const textAlignLast = el.kashidaAlign === 'justify' ? (el.textAlign || 'right') : '';
              
              const verticalJustify = el.verticalAlign === 'middle' ? 'center' : el.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start';
              
              // Use exact font size as in canvas for hidden elements
              const containerWidth = el.width || 300;
              const containerHeight = el.height || 80;
              const baseFontSize = el.fontSize;
              
              const style = [
                `position:absolute`,
                `left:${el.x}px`,
                `top:${el.y}px`,
                `width:${containerWidth}px`,
                `height:${containerHeight}px`,
                `font-size:${baseFontSize}px`,
                `color:${el.color}`,
                `font-family:${el.fontFamily || 'Cairo, sans-serif'}`,
                `font-weight:${el.fontWeight || 400}`,
                `text-align:${textAlign}`,
                textAlignLast ? `text-align-last:${textAlignLast}` : '',
                el.kashidaAlign === 'justify' ? `text-justify:inter-word` : '',
                `direction:${el.direction || 'rtl'}`,
                `letter-spacing:${el.letterSpacing || 0}px`,
                `line-height:${el.lineHeight || 1.5}`,
                `text-transform:${el.textTransform || 'none'}`,
                `background:${el.backgroundColor || 'transparent'}`,
                `text-shadow:${el.textShadow || 'none'}`,
                `border-radius:6px`,
                `padding:4px 10px`,
                `box-sizing:border-box`,
                `display:flex`,
                `flex-direction:column`,
                `justify-content:${verticalJustify}`,
                `overflow:hidden`,
                `word-break:break-word`,
                `white-space:pre-wrap`,
              ].filter(s => s).join(';');
              childrenHtml += `<div style="${style}">${el.text}</div>`;
            } else if (el.type === 'image') {
              const style = [
                `position:absolute`,
                `left:${el.x}px`,
                `top:${el.y}px`,
                `width:${el.width}px`,
                `height:${el.height}px`,
                `border-radius:4px`,
                `object-fit:${el.objectFit || 'cover'}`,
              ].join(';');
              childrenHtml += `<img src="${el.src}" alt="img" style="${style}" />`;
            }
            return; // Skip to next element
          }
          
          if (el.type === 'text') {
            // استخدم الـ name المخصص مباشرة مع text- prefix
            const name = el.name || `text-${el.order !== undefined ? el.order + 1 : 1}`;
            const textAlign = el.kashidaAlign === 'justify' ? 'justify' : (el.textAlign || 'right');
            const textAlignLast = el.kashidaAlign === 'justify' ? (el.textAlign || 'right') : '';
            const verticalJustify = el.verticalAlign === 'middle' ? 'center' : el.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start';
            
            // Use exact font size as in canvas, with overflow protection
            const containerWidth = el.width || 300;
            const containerHeight = el.height || 80;
            const baseFontSize = el.fontSize;
            
            const style = [
              `position:absolute`,
              `left:${el.x}px`,
              `top:${el.y}px`,
              `width:${containerWidth}px`,
              `height:${containerHeight}px`,
              `font-size:${baseFontSize}px`,
              `color:${el.color}`,
              `font-family:${el.fontFamily || 'Cairo, sans-serif'}`,
              `font-weight:${el.fontWeight || 400}`,
              `text-align:${textAlign}`,
              textAlignLast ? `text-align-last:${textAlignLast}` : '',
              el.kashidaAlign === 'justify' ? `text-justify:inter-word` : '',
              `direction:${el.direction || 'rtl'}`,
              `letter-spacing:${el.letterSpacing || 0}px`,
              `line-height:${el.lineHeight || 1.5}`,
              `text-transform:${el.textTransform || 'none'}`,
              `background:${el.backgroundColor || 'transparent'}`,
              `text-shadow:${el.textShadow || 'none'}`,
              `border-radius:6px`,
              `padding:4px 10px`,
              `box-sizing:border-box`,
              `display:flex`,
              `flex-direction:column`,
              `justify-content:${verticalJustify}`,
              `overflow:hidden`,
              `word-break:break-word`,
              `white-space:pre-wrap`,
            ].filter(s => s).join(';');
            childrenHtml += `<div data-key="${name}" style="${style}">{{${name}}}</div>`;
          }
          if (el.type === 'image') {
            // استخدم الـ name المخصص مباشرة مع img- prefix
            const name = el.name || `img-${el.order !== undefined ? el.order + 1 : 1}`;
            const style = [
              `position:absolute`,
              `left:${el.x}px`,
              `top:${el.y}px`,
              `width:${el.width}px`,
              `height:${el.height}px`,
              `border-radius:4px`,
              `object-fit:${el.objectFit || 'cover'}`,
            ].join(';');
            childrenHtml += `<img data-key="${name}" src="{{${name}}}" alt="img" style="${style}" />`;
          }
        });

        // إنشاء @font-face rules للخطوط المخصصة
        const customFontFaces = customFonts.map(font => {
          const familyName = font.font_family_css.replace(/['"]/g, '').split(',')[0].trim();
          return `@font-face {
            font-family: '${familyName}';
            src: url('${font.font_file_url}') format('truetype');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }`;
        }).join('\n');

        const autoFitScript = `<script>
(function autoFitText() {
  const textElements = document.querySelectorAll('[data-key]');
  textElements.forEach(function(el) {
    const element = el;
    const originalFontSize = parseFloat(getComputedStyle(element).fontSize);
    
    // Function to check if text overflows
    function checkOverflow() {
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      return scrollHeight > clientHeight;
    }
    
    // Binary search to find optimal font size
    function fitFontSize() {
      let minSize = Math.floor(originalFontSize * 0.3);
      let maxSize = Math.floor(originalFontSize * 1.5);
      let optimalSize = originalFontSize;
      
      // Start with original size
      element.style.fontSize = originalFontSize + 'px';
      
      // If it overflows, find the right size
      if (checkOverflow()) {
        for (let i = 0; i < 10; i++) {
          const testSize = Math.floor((minSize + maxSize) / 2);
          element.style.fontSize = testSize + 'px';
          
          if (checkOverflow()) {
            maxSize = testSize;
          } else {
            optimalSize = testSize;
            minSize = testSize;
          }
        }
      } else {
        // If it doesn't overflow, can we make it bigger?
        for (let i = 0; i < 10; i++) {
          const testSize = Math.floor((originalFontSize + maxSize) / 2);
          element.style.fontSize = testSize + 'px';
          
          if (!checkOverflow()) {
            optimalSize = testSize;
          } else {
            maxSize = testSize;
            element.style.fontSize = optimalSize + 'px';
            break;
          }
        }
      }
      
      element.style.fontSize = optimalSize + 'px';
    }
    
    fitFontSize();
  });
})();
</script>`;
        
        return `<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>${projectName || 'Template'}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;700&family=Amiri:wght@400;700&family=Tajawal:wght@300;400;700&family=Noto+Naskh+Arabic:wght@400;700&family=Changa:wght@300;400;700&family=Almarai:wght@300;400;700&family=Reem+Kufi:wght@400;700&family=Lalezar&family=Lateef:wght@400;700&family=Noto+Sans+Arabic:wght@300;400;700&display=swap" rel="stylesheet"><style>html,body{margin:0;padding:0;background:#f7f7f7}#canvas{${containerStyle}}${customFontFaces ? '\n' + customFontFaces : ''}</style></head><body><div id="canvas">${childrenHtml}</div>${autoFitScript}</body></html>`;
      };

      const htmlTemplate = generateHTML();

      // 🖼️ توليد صورة Cover من HTML Template باستخدام Render API
      let coverUrl = projectData?.cover;
      
      try {
        console.log('🎨 Generating cover from HTML template using Render API...');
        
        // تحضير الخطوط المخصصة لإرسالها إلى سيرفر الرندر
        const fontsForRender = customFonts.map(font => ({
          name: font.font_name,
          url: font.font_file_url
        }));

        console.log('🔤 Sending fonts to render server:', fontsForRender.length, 'fonts');
        
        if (fontsForRender.length > 0) {
          console.log('📝 Font details:');
          fontsForRender.forEach((font, index) => {
            console.log(`   ${index + 1}. ${font.name} - ${font.url}`);
          });
        }

        // إرسال HTML للـ Render API مع الخطوط المخصصة
        const result = await generateImage({
          html: htmlTemplate,
          width: canvasWidth,
          height: canvasHeight,
          fonts: fontsForRender,
          quality: 90
        });
        
        if (result.success && result.image) {
          console.log('✅ Cover image generated successfully from HTML');
          
          // حذف الصورة القديمة أولاً
          if (projectData?.cover) {
            try {
              const oldCoverUrl = projectData.cover;
              if (oldCoverUrl.includes('/project-images/')) {
                const oldPath = oldCoverUrl.split('/project-images/')[1].split('?')[0];
                console.log('🗑️ Deleting old cover:', oldPath);
                
                await supabase.storage
                  .from('project-images')
                  .remove([oldPath]);
                
                console.log('✅ Old cover deleted');
              }
            } catch (delErr) {
              console.warn('⚠️ Error deleting old cover:', delErr);
            }
          }
          
          // رفع صورة Cover الجديدة
          const blob = await (await fetch(result.image)).blob();
          const fileName = `${user.id}/${projectId}/cover.png`;
          
          console.log('☁️ Uploading cover to Storage:', fileName);
          const { error: uploadError } = await supabase.storage
            .from('project-images')
            .upload(fileName, blob, { upsert: true });
          
          if (!uploadError) {
            const { data: urlData } = supabase.storage
              .from('project-images')
              .getPublicUrl(fileName);
            coverUrl = urlData.publicUrl;
            console.log('✅ Cover uploaded:', coverUrl);
          } else {
            console.warn('⚠️ Cover upload failed:', uploadError);
          }
        } else {
          console.warn('⚠️ Cover generation failed:', result.error);
        }
      } catch (err) {
        console.error('❌ Error generating cover:', err);
      }

      const updateData: any = {
        editor_data: {
          elements: processedElements, // Use processed elements with uploaded images
          canvasWidth,
          canvasHeight,
          canvasOrientation: canvasWidth > canvasHeight ? 'landscape' : 'portrait',
        },
        html_template: htmlTemplate,
        updated_at: new Date().toISOString(),
      };
      
      // إضافة cover (صورة الغلاف المولدة من الرندر)
      if (coverUrl) {
        updateData.cover = coverUrl;
        console.log('💾 Saving cover URL to database');
      }

      const { error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', projectId);

      if (error) throw error;

      // Show success notification
      setSaveSuccess(true);
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveSuccess(false), 2000);

      return { success: true };
    } catch (err: any) {
      console.error('Error saving project:', err);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [projectId, user]);

  // Mark as having unsaved changes
  const markUnsaved = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  // Warn before leaving if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = t('pages.editor.beforeUnload');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, t]);

  return {
    projectId, // Export projectId
    projectData,
    loading,
    saving,
    saveSuccess,
    hasUnsavedChanges,
    accessDenied,
    saveProject,
    markUnsaved,
  };
};

